import { useEffect, useRef, useState } from 'react';

/**
 * Hijacks vertical mouse-wheel input on desktop and converts it to smooth
 * horizontal translation of a track element. On mobile, the wrapper falls
 * back to native horizontal scroll. Returns refs to attach plus a 0–1
 * progress value for scroll indicators.
 *
 * @returns {{
 *   wrapRef: React.RefObject<HTMLDivElement>,
 *   trackRef: React.RefObject<HTMLDivElement>,
 *   progress: number
 * }}
 */
export function useHorizontalScroll() {
  const wrapRef = useRef(null);
  const trackRef = useRef(null);
  const [progress, setProgress] = useState(0);

  const targetX = useRef(0);
  const currentX = useRef(0);
  const maxX = useRef(0);

  useEffect(() => {
    const wrap = wrapRef.current;
    const track = trackRef.current;
    if (!wrap || !track) return undefined;

    const isMobile = () => window.matchMedia('(max-width: 768px)').matches;

    const recompute = () => {
      maxX.current = Math.max(0, track.scrollWidth - window.innerWidth);
      targetX.current = Math.min(targetX.current, maxX.current);
    };
    recompute();
    window.addEventListener('resize', recompute);

    const onWheel = (e) => {
      if (isMobile()) return;
      const delta = Math.abs(e.deltaY) > Math.abs(e.deltaX) ? e.deltaY : e.deltaX;
      const atStart = currentX.current <= 1 && delta < 0;
      const atEnd = currentX.current >= maxX.current - 1 && delta > 0;
      if (atStart || atEnd) return;
      e.preventDefault();
      targetX.current = Math.max(0, Math.min(maxX.current, targetX.current + delta));
    };

    let touchStartX = 0;
    const onTouchStart = (e) => {
      if (isMobile()) return;
      touchStartX = e.touches[0].clientX;
    };
    const onTouchMove = (e) => {
      if (isMobile()) return;
      const dx = touchStartX - e.touches[0].clientX;
      touchStartX = e.touches[0].clientX;
      targetX.current = Math.max(0, Math.min(maxX.current, targetX.current + dx * 1.4));
    };

    wrap.addEventListener('wheel', onWheel, { passive: false });
    wrap.addEventListener('touchstart', onTouchStart, { passive: true });
    wrap.addEventListener('touchmove', onTouchMove, { passive: true });

    let raf;
    const tick = () => {
      currentX.current += (targetX.current - currentX.current) * 0.085;
      if (Math.abs(targetX.current - currentX.current) < 0.05) {
        currentX.current = targetX.current;
      }
      if (isMobile()) {
        track.style.transform = '';
      } else {
        track.style.transform = `translate3d(${-currentX.current}px, 0, 0)`;
      }
      const p = maxX.current > 0 ? currentX.current / maxX.current : 0;
      setProgress(p);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    return () => {
      window.removeEventListener('resize', recompute);
      wrap.removeEventListener('wheel', onWheel);
      wrap.removeEventListener('touchstart', onTouchStart);
      wrap.removeEventListener('touchmove', onTouchMove);
      cancelAnimationFrame(raf);
    };
  }, []);

  return { wrapRef, trackRef, progress };
}
